import React, { useCallback, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { i18next, view } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import elementSettings from 'src/js/modules/element-settings';
import BorderControl from '../components/border-control';
import ColorSchemeControl from '../components/color-scheme-control';
import WidthControl from '../components/width-control';
import CornersControl from '../components/corners-control';
import CornersSectionControl from '../components/corners-section-control';
import HorizontalSpacingControl from '../components/horizontal-spacing-control';
import SpacingSectionControl from '../components/spacing-section-control';
import ShadowControl from '../components/shadow-control';
import VerticalSpacingControl from '../components/vertical-spacing-control';
import useMigrateLegacyAttributes from '../attribute-migration/use-migrate-legacy-attributes';
import controlIds from '../constants/control-ids';
import getColumnControlSettings from '../settings/column-control-settings';
import createSubmitTraitsColumnAppearanceHandler from '../helpers/create-submit-traits-column-appearance-handler';
import generateColumnAppearanceInitialState from '../helpers/generate-column-appearance-initial-state';

const { Divider } = designSystem;
const {
  Context: ElementSettingsContext,
  components: { Section, ActionRadioGroupControl },
  constants: { stages },
  helpers: { createBatch },
  hooks: { useMountChunk },
} = elementSettings;

const EVENT_TRAITS_PREFIX = 'column';

const DESKTOP_ONLY_CONTROL_IDS = [
  controlIds.COLOR_SCHEME,
  controlIds.HORIZONTAL_CONTENT_ALIGNMENT,
  controlIds.VERTICAL_CONTENT_ALIGNMENT,
];

const createControlVisibilityChecker = (deviceMode) => (controlId) =>
  ![view.deviceModes.TABLET, view.deviceModes.MOBILE].includes(deviceMode?.active) ||
  !DESKTOP_ONLY_CONTROL_IDS.includes(controlId);

const ColumnAppearanceSettingsContainer = ({ tabId, elementId, settingId }) => {
  const chunkId = tabId;
  const { callbacks, deviceMode, onUpdateState } = useContext(ElementSettingsContext);

  const isBreakpointEditingEnabled = Boolean(deviceMode.active);

  const controlSettings = useMemo(() => getColumnControlSettings(elementId), [elementId]);
  const columnElementIds = controlSettings[controlIds.VERTICAL_SPACING].elementIds;

  const handleUpdateState = useCallback(
    (batch, options = {}) => onUpdateState(batch, { chunkId, ...options }),
    [chunkId, onUpdateState]
  );

  const handleChange = useCallback(
    ({
      controlId,
      elementIds,
      targets,
      value,
      skipApplyChanges = false,
      skipTracking = false,
      shouldScrollToTarget = true,
      batch: providedBatch,
    }) => {
      const batch = providedBatch || createBatch({ elementIds, targets, value });

      handleUpdateState(batch, { skipApplyChanges, shouldScrollToTarget });
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, settingId, handleUpdateState]
  );

  const handleBatchChange = useCallback(
    ({ batch, controlId, skipTracking = false }) => {
      handleUpdateState(batch);
      callbacks.onControlClick({ controlId, settingId, skipTracking });
    },
    [callbacks, handleUpdateState, settingId]
  );

  const handleToggleableSectionChange = useCallback(
    ({ batch, controlId, value, skipTracking = false, shouldScrollToTarget = true }) => {
      handleUpdateState(batch, { shouldScrollToTarget });
      callbacks.onToggleClick({ controlId, settingId, isActive: value, skipTracking });
    },
    [callbacks, handleUpdateState, settingId]
  );

  const traitsHandler = useMemo(
    () =>
      createSubmitTraitsColumnAppearanceHandler({
        prefix: EVENT_TRAITS_PREFIX,
        isBreakpointEditingEnabled,
      }),
    [isBreakpointEditingEnabled]
  );

  const { isChunkMounted } = useMountChunk(chunkId, {
    tabId,
    initialState: generateColumnAppearanceInitialState(controlSettings),
    stageHandlers: [
      {
        stage: stages.SUBMIT_TRAITS_GENERATION,
        handler: traitsHandler,
      },
    ],
  });

  useMigrateLegacyAttributes({
    enabled: isChunkMounted,
    columnElementIds,
    onUpdateState: handleUpdateState,
  });

  if (!isChunkMounted) return null;

  const isControlVisible = createControlVisibilityChecker(deviceMode);

  return (
    <>
      {isControlVisible(controlIds.COLOR_SCHEME) && (
        <>
          <ColorSchemeControl
            {...controlSettings[controlIds.COLOR_SCHEME]}
            onChange={handleBatchChange}
          />
          <Divider appearance="light-grey" />
        </>
      )}
      {(isControlVisible(controlIds.HORIZONTAL_CONTENT_ALIGNMENT) ||
        isControlVisible(controlIds.VERTICAL_CONTENT_ALIGNMENT)) && (
        <>
          <Section title={i18next.t('Content alignment')}>
            {isControlVisible(controlIds.HORIZONTAL_CONTENT_ALIGNMENT) && (
              <ActionRadioGroupControl
                {...controlSettings[controlIds.HORIZONTAL_CONTENT_ALIGNMENT]}
                nested
                onChange={handleChange}
              />
            )}
            {isControlVisible(controlIds.VERTICAL_CONTENT_ALIGNMENT) && (
              <ActionRadioGroupControl
                {...controlSettings[controlIds.VERTICAL_CONTENT_ALIGNMENT]}
                nested
                onChange={handleChange}
              />
            )}
          </Section>
          <Divider appearance="light-grey" />
        </>
      )}
      <Section title={i18next.t('Layout')}>
        <WidthControl
          elementIds={controlSettings[controlIds.COLUMN_WIDTH].elementIds}
          targets={controlSettings[controlIds.COLUMN_WIDTH].targets}
          nested
          onChange={handleChange}
        />
      </Section>
      <Divider appearance="light-grey" />
      <SpacingSectionControl
        elementIds={{
          [controlIds.VERTICAL_SPACING]: controlSettings[controlIds.VERTICAL_SPACING].elementIds,
          [controlIds.HORIZONTAL_SPACING]:
            controlSettings[controlIds.HORIZONTAL_SPACING].elementIds,
        }}
        targets={{
          [controlIds.VERTICAL_SPACING]: controlSettings[controlIds.VERTICAL_SPACING].targets,
          [controlIds.HORIZONTAL_SPACING]: controlSettings[controlIds.HORIZONTAL_SPACING].targets,
        }}
        onChange={handleToggleableSectionChange}
      >
        <VerticalSpacingControl
          elementIds={controlSettings[controlIds.VERTICAL_SPACING].elementIds}
          targets={controlSettings[controlIds.VERTICAL_SPACING].targets}
          onChange={handleChange}
        />
        <HorizontalSpacingControl
          elementIds={controlSettings[controlIds.HORIZONTAL_SPACING].elementIds}
          targets={controlSettings[controlIds.HORIZONTAL_SPACING].targets}
          onChange={handleChange}
        />
      </SpacingSectionControl>
      <Divider appearance="light-grey" />
      <CornersSectionControl
        elementIds={controlSettings[controlIds.CORNERS].elementIds}
        targets={controlSettings[controlIds.CORNERS].targets}
        onChange={handleToggleableSectionChange}
      >
        <CornersControl
          elementIds={controlSettings[controlIds.CORNERS].elementIds}
          targets={controlSettings[controlIds.CORNERS].targets}
          onChange={handleChange}
        />
      </CornersSectionControl>
      <Divider appearance="light-grey" />
      {isControlVisible(controlIds.BORDER) && (
        <>
          <BorderControl
            elementIds={controlSettings[controlIds.BORDER].elementIds}
            targets={controlSettings[controlIds.BORDER].targets}
            onChange={handleToggleableSectionChange}
          />
          <Divider appearance="light-grey" />
        </>
      )}
      {isControlVisible(controlIds.SHADOW) && (
        <ShadowControl
          elementIds={controlSettings[controlIds.SHADOW].elementIds}
          targets={controlSettings[controlIds.SHADOW].targets}
          onChange={handleToggleableSectionChange}
        />
      )}
    </>
  );
};

ColumnAppearanceSettingsContainer.propTypes = {
  tabId: PropTypes.string.isRequired,
  elementId: PropTypes.string.isRequired,
  settingId: PropTypes.string.isRequired,
};

export default ColumnAppearanceSettingsContainer;
